import React, { useState, useEffect } from "react";
import "./comboproduct.css";
import ModalCloseButton from "../../ModalCloseButton/ModalCloseButton";

const ComboProductModifier = ({
  open,
  onClose,
  product,
  modifierGroups = [],
  selectedModifiers,
  onSelectModifier,
  onConfirm,
  previousSelections = []
}) => {
  const [selected, setSelected] = useState({});
  const [toastMessage, setToastMessage] = useState("");

  useEffect(() => {
    if (open) {
      if (previousSelections && previousSelections.length > 0) {
        // Rebuild the selected map from previously confirmed selections
        const rebuilt = {};
        for (const group of modifierGroups) {
          for (const modSet of group.modifier || []) {
            const setKey = `${group.product_id}_${modSet.id}`;
            for (const detail of modSet.modifier_details || []) {
              const prev = previousSelections.find(p => Number(p.id) === Number(detail.id));
              if (prev && prev.qty > 0) {
                if (!rebuilt[setKey]) rebuilt[setKey] = {};
                rebuilt[setKey][detail.id] = prev.qty;
              }
            }
          }
        }
        setSelected(rebuilt);
      } else {
        setSelected({});
      }
    }
  }, [open, product?.id]);
  const handleQuantityChange = (setId, itemId, max, itemLimit, type) => {
    setSelected((prev) => {
      const setItems = prev[setId] || {};
      const currentQty = setItems[itemId] || 0;

      let newQty = currentQty;

      if (type === "increment") {
        const totalSelected = Object.values(setItems).reduce((a, b) => a + b, 0);
        const effectiveItemLimit = (itemLimit === null || itemLimit === undefined) ? (max || Infinity) : itemLimit;
        if (currentQty >= effectiveItemLimit) {
          setToastMessage(`You reached modifier limit ${effectiveItemLimit} items`);
          return prev;
        }

        if (totalSelected >= (max || Infinity)) return prev;
        newQty = currentQty + 1;
        setToastMessage("");
      }

      if (type === "decrement") {
        newQty = currentQty > 0 ? currentQty - 1 : 0;
        setToastMessage("");
      }

      const updatedSet = { ...setItems };

      if (newQty === 0) {
        delete updatedSet[itemId];
      } else {
        updatedSet[itemId] = newQty;
      }

      return {
        ...prev,
        [setId]: updatedSet,
      };
    });
  };

  if (!open) return null;

  return (
    <div className="combo-popup-overlay">
      <div className={`combo-popup${(!modifierGroups || modifierGroups.length === 0 || modifierGroups.every(
        (group) => !(group.modifier || []).some((modSet) => (modSet.modifier_details || []).length)
      )) ? ' combo-popup--empty' : ''
        }`}>
        {/* Header */}
        <div className="combo-popup-header">
          <h2>Choose Modifier</h2>
          <ModalCloseButton onClick={onClose} />
        </div>

        <div className="combo-modifier-section">
          {!modifierGroups ||
            modifierGroups.length === 0 ||
            modifierGroups.every(
              (group) =>
                !(group.modifier || []).some(
                  (modSet) => (modSet.modifier_details || []).length
                )
            ) ? (
            <p className="no-modifiers">No modifiers available.</p>
          ) : (
            modifierGroups.map((group, i) => (
              <div key={i} className="modifier-group-box">
                {(group.modifier || []).map((modSet, setIndex) => (
                  <div key={setIndex} className="modifier-set-block">
                    <div className="modifier-set-header">
                      <h3 className="group-name">{modSet.name}</h3>

                      {(() => {
                        const setKey = `${group.product_id}_${modSet.id}`;
                        const setItems = selected[setKey] || {};
                        const totalSelected = Object.values(setItems).reduce((a, b) => a + b, 0);
                        const hasLimit = modSet.order_limit > 0;
                        const hasMin = modSet.order_limit_at_least > 0;

                        if (hasLimit && hasMin) {
                          const clampedMin = Math.min(totalSelected, modSet.order_limit_at_least);
                          return (
                            <span>- {totalSelected}/{modSet.order_limit} (min-{clampedMin}/{modSet.order_limit_at_least})</span>
                          );
                        } else if (hasLimit) {
                          return <span>- {totalSelected}/{modSet.order_limit} (optional)</span>;
                        } else if (hasMin) {
                          const clampedMin = Math.min(totalSelected, modSet.order_limit_at_least);
                          return <span>(min-{clampedMin}/{modSet.order_limit_at_least})</span>;
                        } else {
                          return <span className="optional-text">( Optional )</span>;
                        }
                      })()}

                    </div>
                    <table className="modifier-table">
                      <tbody>
                        {(modSet.modifier_details || []).map((item, idx) => (
                          <tr key={idx} className="modifier-item-row">
                            <td className="modifier-left">
                              <span className="modifier-name">{item.name}</span>
                            </td>

                            <td>
                              <div className="qty-control">
                                <button
                                  className="qty-btn"
                                  onClick={() =>
                                    handleQuantityChange(
                                      `${group.product_id}_${modSet.id}`,
                                      item.id,
                                      modSet.order_limit,
                                      null,
                                      "decrement"
                                    )
                                  }
                                >
                                  -
                                </button>

                                <span className="qty-value">
                                  {selected[`${group.product_id}_${modSet.id}`]?.[item.id] || 0}
                                </span>

                                <button
                                  className="qty-btn plus"
                                  onClick={() =>
                                    handleQuantityChange(
                                      `${group.product_id}_${modSet.id}`,
                                      item.id,
                                      modSet.order_limit,
                                      item.modifier_order_limit,
                                      "increment"
                                    )
                                  }
                                >
                                  +
                                </button>
                              </div>
                            </td>

                            <td className="modifier-price">
                              {(() => {
                                const qty = selected[`${group.product_id}_${modSet.id}`]?.[item.id] || 0;
                                const total = parseFloat(item.sell_price_inc_tax) * (qty > 0 ? qty : 1);
                                return `+$${total.toFixed(2)}`;
                              })()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
        <div className="mod-footer">
          {toastMessage && (
            <div className="error-text">
              {toastMessage}
            </div>
          )}
          <button
            className="submit"

            onClick={() => {
              for (const group of modifierGroups) {
                for (const modSet of group.modifier || []) {

                  const setItems = selected[`${group.product_id}_${modSet.id}`] || {};
                  const totalSelected = Object.values(setItems).reduce(
                    (a, b) => a + b,
                    0
                  );

                  if (
                    modSet.order_limit_at_least &&
                    totalSelected < modSet.order_limit_at_least
                  ) {
                    setToastMessage(
                      `Please select at least ${modSet.order_limit_at_least} items in ${modSet.name}.`
                    );
                    return;
                  }

                  if (
                    modSet.order_limit &&
                    totalSelected > modSet.order_limit
                  ) {
                    setToastMessage(
                      `Maximum ${modSet.order_limit} items allowed in ${modSet.name}.`
                    );
                    return;
                  }
                }
              }

              setToastMessage("");

              // Build selectedModifiers directly here — modifierGroups + selected are both in scope.
              // No ID reconstruction needed at all.
              const productModifiers = [];
              for (const group of modifierGroups) {
                for (const modSet of group.modifier || []) {
                  const setKey = `${group.product_id}_${modSet.id}`;
                  const setItems = selected[setKey] || {};
                  for (const item of modSet.modifier_details || []) {
                    const qty = Number(setItems[item.id] || 0);
                    if (qty > 0) {
                      productModifiers.push({ ...item, qty, quantity: qty, checked: true });
                    }
                  }
                }
              }

              onConfirm({
                selected,
                productModifiers,
                totalPrice: productModifiers.reduce(
                  (acc, item) => acc + item.qty * Number(item.sell_price_inc_tax || 0),
                  0
                ),
              });
            }}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export default ComboProductModifier;
